import { Controller } from "@hotwired/stimulus"

// Single-member role picker for Real Estate team fields.
// Shows a popover with search + member list (radio-style, single select).
// Selecting a member sets the hidden field value and shows the name in the display.
// "Invite New Member" opens inline email input with MX validation.

export default class extends Controller {
  static targets = [
    "display", "hidden", "popover", "popoverSearch", "popoverList",
    "inviteModal", "inviteEmailInput", "inviteEmailError", "inviteSubmitBtn",
    "chevron", "clearBtn"
  ]

  static values = {
    members: Array,
    validateEmailUrl: String,
    initialEmail: String,
    initialName: String
  }

  connect() {
    this._onClickOutside = (e) => {
      if (this.hasPopoverTarget && !this.element.contains(e.target) && !this.popoverTarget.contains(e.target)) {
        this._closePopover()
      }
    }
    document.addEventListener("click", this._onClickOutside)

    // Close popover when the modal body scrolls (fixed popover won't follow)
    this._scrollContainer = this.element.closest(".overflow-y-auto")
    if (this._scrollContainer) {
      this._onScroll = () => this._closePopover()
      this._scrollContainer.addEventListener("scroll", this._onScroll)
    }

    // Pre-populate from initial values (e.g. existing role assignments)
    if (this.initialEmailValue) {
      this._setValue(this.initialEmailValue, this.initialNameValue || this.initialEmailValue)
    } else {
      this._updateClearVisibility(false)
    }
  }

  disconnect() {
    document.removeEventListener("click", this._onClickOutside)
    if (this._scrollContainer && this._onScroll) {
      this._scrollContainer.removeEventListener("scroll", this._onScroll)
    }
  }

  // ── Popover ─────────────────────────────────────────────────────────

  togglePopover() {
    if (!this.hasPopoverTarget) return
    if (this.popoverTarget.classList.contains("hidden")) {
      this._openPopover()
    } else {
      this._closePopover()
    }
  }

  _openPopover() {
    this._renderList("")
    this.popoverTarget.classList.remove("hidden")
    this._positionPopover()
    if (this.hasPopoverSearchTarget) {
      this.popoverSearchTarget.value = ""
      this.popoverSearchTarget.focus()
    }
  }

  _positionPopover() {
    const trigger = this.displayTarget
    const rect = trigger.getBoundingClientRect()
    const popover = this.popoverTarget

    popover.style.left = `${rect.left}px`
    popover.style.width = `${rect.width}px`

    // Show below the trigger; if it would overflow the viewport, show above
    const spaceBelow = window.innerHeight - rect.bottom
    const popoverHeight = popover.offsetHeight || 300

    if (spaceBelow >= popoverHeight || spaceBelow >= rect.top) {
      popover.style.top = `${rect.bottom + 4}px`
      popover.style.bottom = "auto"
    } else {
      popover.style.bottom = `${window.innerHeight - rect.top + 4}px`
      popover.style.top = "auto"
    }
  }

  _closePopover() {
    if (this.hasPopoverTarget) {
      this.popoverTarget.classList.add("hidden")
    }
  }

  filterMembers() {
    const query = this.popoverSearchTarget.value.trim().toLowerCase()
    this._renderList(query)
  }

  _renderList(query) {
    if (!this.hasPopoverListTarget) return

    const members = this.membersValue || []
    const currentEmail = this.hasHiddenTarget ? this.hiddenTarget.value : ""

    const filtered = members.filter(m => {
      if (!query) return true
      return `${m.name} ${m.email}`.toLowerCase().includes(query)
    })

    let html = ""
    if (filtered.length === 0 && !query) {
      html = `<div class="px-3 py-4 text-xs text-gray-400 text-center">No members found</div>`
    } else if (filtered.length === 0) {
      html = `<div class="px-3 py-4 text-xs text-gray-400 text-center">No matching members</div>`
    } else {
      html = filtered.map(m => {
        const selected = m.email === currentEmail
        return `<div data-action="click->re-role-field#selectMember"
                     data-email="${this._esc(m.email)}"
                     data-name="${this._esc(m.name)}"
                     data-user-id="${m.id || ''}"
                     class="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 cursor-pointer transition-colors ${selected ? 'bg-brand/5' : ''}">
          <div class="w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${selected ? 'border-brand bg-brand' : 'border-gray-300'}">
            ${selected ? '<svg class="w-full h-full text-white" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="8" r="3"/></svg>' : ''}
          </div>
          <div class="flex-1 min-w-0">
            <div class="text-sm font-medium text-gray-900">${this._esc(m.name || m.email)}</div>
            <div class="text-xs text-text-muted">${this._esc(m.email)}</div>
          </div>
        </div>`
      }).join("")
    }

    this.popoverListTarget.innerHTML = html
  }

  // ── Select Member ─────────────────────────────────────────────────

  selectMember(event) {
    const el = event.currentTarget
    const email = el.dataset.email
    const name = el.dataset.name
    const userId = el.dataset.userId || ""

    this._setValue(email, name, userId)
    this._closePopover()
  }

  _setValue(email, name, userId) {
    if (this.hasHiddenTarget) {
      this.hiddenTarget.value = email
      if (userId) {
        this.hiddenTarget.dataset.userId = userId
      } else {
        // Try to resolve from members list
        const members = this.membersValue || []
        const member = members.find(m => m.email === email)
        if (member && member.id) {
          this.hiddenTarget.dataset.userId = member.id
        } else {
          // Clear stale user ID when assigning a new/unknown email
          delete this.hiddenTarget.dataset.userId
        }
      }
    }
    if (this.hasDisplayTarget) {
      const displayName = name && name !== email ? `${name} (${email})` : email
      this.displayTarget.value = displayName
      this.displayTarget.classList.remove("text-text-muted")
      this.displayTarget.classList.add("text-gray-900")
    }
    this._updateClearVisibility(true)
  }

  clearValue(event) {
    if (event) event.stopPropagation()
    if (this.hasHiddenTarget) {
      this.hiddenTarget.value = ""
      delete this.hiddenTarget.dataset.userId
    }
    if (this.hasDisplayTarget) {
      this.displayTarget.value = ""
      this.displayTarget.classList.remove("text-gray-900")
      this.displayTarget.classList.add("text-text-muted")
    }
    this._updateClearVisibility(false)
    this._closePopover()
  }

  // Swap the chevron / clear (×) button depending on whether a value is set.
  _updateClearVisibility(hasValue) {
    if (this.hasClearBtnTarget) this.clearBtnTarget.classList.toggle("hidden", !hasValue)
    if (this.hasChevronTarget) this.chevronTarget.classList.toggle("hidden", hasValue)
  }

  // ── Invite New Member Modal ──────────────────────────────────────

  showInviteModal() {
    if (!this.hasInviteModalTarget) return
    this._closePopover()
    this.inviteModalTarget.classList.remove("hidden")
    if (this.hasInviteEmailInputTarget) {
      this.inviteEmailInputTarget.value = ""
      this.inviteEmailInputTarget.classList.remove("border-red-400", "ring-1", "ring-red-400")
      setTimeout(() => this.inviteEmailInputTarget.focus(), 50)
    }
    if (this.hasInviteEmailErrorTarget) {
      this.inviteEmailErrorTarget.classList.add("hidden")
    }
    if (this.hasInviteSubmitBtnTarget) {
      this.inviteSubmitBtnTarget.disabled = false
      this.inviteSubmitBtnTarget.textContent = "Add"
    }
  }

  closeInviteModal() {
    if (this.hasInviteModalTarget) {
      this.inviteModalTarget.classList.add("hidden")
    }
  }

  inviteEmailKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.submitInviteEmail()
    } else if (event.key === "Escape") {
      this.closeInviteModal()
    }
  }

  async submitInviteEmail() {
    if (!this.hasInviteEmailInputTarget) return

    const email = this.inviteEmailInputTarget.value.trim().toLowerCase()

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this._showInviteError("Please enter a valid email address.")
      return
    }

    // Backend MX validation
    if (this.validateEmailUrlValue) {
      this._setInviteLoading(true)
      try {
        const url = `${this.validateEmailUrlValue}?email=${encodeURIComponent(email)}`
        const resp = await fetch(url, { headers: { "Accept": "application/json" } })
        if (resp.ok) {
          const data = await resp.json()
          if (!data.is_valid) {
            this._setInviteLoading(false)
            this._showInviteError("This email address doesn't exist. Please check for typos.")
            return
          }
        }
      } catch { /* proceed */ }
      this._setInviteLoading(false)
    }

    this._setValue(email, email)
    this.closeInviteModal()
  }

  _showInviteError(msg) {
    if (!this.hasInviteEmailErrorTarget) return
    this.inviteEmailErrorTarget.textContent = msg
    this.inviteEmailErrorTarget.classList.remove("hidden")
    this.inviteEmailInputTarget?.classList.add("border-red-400", "ring-1", "ring-red-400")
  }

  _setInviteLoading(loading) {
    if (!this.hasInviteSubmitBtnTarget) return
    this.inviteSubmitBtnTarget.disabled = loading
    this.inviteSubmitBtnTarget.textContent = loading ? "Validating..." : "Add"
  }

  // ── Utilities ─────────────────────────────────────────────────────

  // Safe in BOTH text and attribute position — quotes must be escaped too,
  // or a value carrying an inch mark (`6" VERTICAL CURB`) closes its own
  // attribute and swallows the rest of the tag, data-* attributes included.
  _esc(str) {
    const d = document.createElement("div")
    d.textContent = str || ""
    return d.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
}
